import { useRef, useState, useCallback } from 'react';
import { MapContainer, TileLayer } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import {
  Alert, Box, Button, Chip, CircularProgress, Divider, IconButton, Slider, Stack, Tooltip, Typography,
} from '@mui/material';
import Iconify from '../iconify';
import UoHRasterLayer from './UoHRasterLayer';
import TERRRAPointsLayer from './TERRRAPointsLayer';
import StrataLayer from './StrataLayer';
import PixelInspector from './PixelInspector';
import LayerToggle from './LayerToggle';
import UploadButton from './UploadButton';

const SATELLITE_URL = 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}';
const SATELLITE_ATTR = 'Tiles &copy; Esri';
const CARBON_PRICE_USD = 25;
const ILS_PER_USD = 3.67;
const SIDEBAR_OPEN_KEY = 'flux.map.sidebarOpen';
const SIDEBAR_WIDTH = 280;

const DUMMY_TERRRA_POINTS = {
  type: 'FeatureCollection',
  features: [
    { type: 'Feature', geometry: { type: 'Point', coordinates: [35.8113, 33.1775] }, properties: { label: 'EC Tower (reference)', species: 'Aleppo Pine', age: 15, flux_tco2_ha_yr: 5.2 } },
    { type: 'Feature', geometry: { type: 'Point', coordinates: [35.8115, 33.1765] }, properties: { label: 'S1 — בכא א (mature)', species: 'Aleppo Pine', age: 10, flux_tco2_ha_yr: 5.0 } },
    { type: 'Feature', geometry: { type: 'Point', coordinates: [35.8106, 33.1770] }, properties: { label: 'S2 — בכא ה (mature)', species: 'Cypress', age: 9, flux_tco2_ha_yr: 3.8 } },
    { type: 'Feature', geometry: { type: 'Point', coordinates: [35.8108, 33.1785] }, properties: { label: 'S3 — בכא ג (young)', species: 'Aleppo Pine', age: 6, flux_tco2_ha_yr: 2.9 } },
    { type: 'Feature', geometry: { type: 'Point', coordinates: [35.8120, 33.1780] }, properties: { label: 'S4 — בכא ב/ד (edge)', species: 'Mixed', age: 8, flux_tco2_ha_yr: 2.4 } },
    { type: 'Feature', geometry: { type: 'Point', coordinates: [35.8192, 33.1530] }, properties: { label: 'S5 — בוסטר 1 (plot)', species: 'Aleppo Pine', age: 12, flux_tco2_ha_yr: 4.6 } },
    { type: 'Feature', geometry: { type: 'Point', coordinates: [35.8204, 33.1534] }, properties: { label: 'S6 — בוסטר 2 (plot)', species: 'Aleppo Pine', age: 12, flux_tco2_ha_yr: 4.3 } },
    { type: 'Feature', geometry: { type: 'Point', coordinates: [35.8197, 33.1519] }, properties: { label: 'S7 — בוסטר 15 (pt)', species: 'Mixed', age: 10, flux_tco2_ha_yr: 3.1 } },
    { type: 'Feature', geometry: { type: 'Point', coordinates: [35.8085, 33.1775] }, properties: { label: 'S8 — בכא ה edge', species: 'Cypress', age: 8, flux_tco2_ha_yr: 2.1 } },
  ],
};

const DUMMY_TEST_AREA = {
  type: 'FeatureCollection',
  features: [
    { type: 'Feature', properties: { name: 'בכא א', age: '~15 yr', note: 'מטע ותיק' }, geometry: { type: 'Polygon', coordinates: [[[35.81004614763672,33.17733058531255],[35.8102050282081,33.1761978599883],[35.81177747932791,33.17627922863899],[35.81212498694128,33.1765050418568],[35.81203091350985,33.17731687002667],[35.8114851130064,33.17708408216581],[35.81058700740357,33.17735454421556],[35.81004614763672,33.17733058531255]]] } },
    { type: 'Feature', properties: { name: 'בכא ב', age: '~8 yr', note: '' }, geometry: { type: 'Polygon', coordinates: [[[35.81013287324899,33.17868008411767],[35.81015205640893,33.17780999520539],[35.81192903709665,33.17806067098902],[35.81183620163903,33.17889466302541],[35.81013287324899,33.17868008411767]]] } },
    { type: 'Feature', properties: { name: 'בכא ג', age: '~6-7 yr', note: 'מטע צעיר על פלטה אחידה' }, geometry: { type: 'Polygon', coordinates: [[[35.81023499669457,33.17776406670156],[35.81023168297291,33.17748746385053],[35.81065528128522,33.17737767089476],[35.81194759884033,33.17755232465822],[35.81188831761102,33.17798988297744],[35.81023499669457,33.17776406670156]]] } },
    { type: 'Feature', properties: { name: 'בכא ד', age: '~6-7 yr', note: 'שיפוע קטן ואחיד' }, geometry: { type: 'Polygon', coordinates: [[[35.8101255911368,33.17871661450756],[35.8118176592768,33.1789560445378],[35.81159284136567,33.17940693314859],[35.81012437562605,33.17922020929591],[35.8101255911368,33.17871661450756]]] } },
    { type: 'Feature', properties: { name: 'בכא ה', age: '~9-10 yr', note: '' }, geometry: { type: 'Polygon', coordinates: [[[35.80821022488011,33.17631816965055],[35.80894373057298,33.17650488375131],[35.80928651131585,33.1760868095531],[35.80981826902624,33.17628495079717],[35.80933157970232,33.17729571478388],[35.81009083411484,33.17748550822419],[35.81005563814626,33.17888619148808],[35.80700346879582,33.17802981601744],[35.80821022488011,33.17631816965055]]] } },
    { type: 'Feature', properties: { name: 'בוסטר 1', age: '~12 yr', note: '' }, geometry: { type: 'Polygon', coordinates: [[[35.81922496708515,33.15177772201292],[35.81927033626436,33.15099720453119],[35.81937403858281,33.15079550311535],[35.82070372794981,33.15124106675365],[35.8203161151882,33.1527293906426],[35.81872496023104,33.15247324661009],[35.81922496708515,33.15177772201292]]] } },
    { type: 'Feature', properties: { name: 'בוסטר 2', age: '~12 yr', note: '' }, geometry: { type: 'Polygon', coordinates: [[[35.81871791771893,33.1537203155527],[35.81857817440101,33.1536665046061],[35.81855094525805,33.15270729005291],[35.82035771588588,33.15297477273646],[35.82244672030389,33.15307270058399],[35.82216881100446,33.15376594929955],[35.82162262162981,33.1534793365353],[35.82105871040542,33.15419857305404],[35.82050614855254,33.15422163404169],[35.82025955460664,33.15386472155937],[35.81988506018713,33.15387362815393],[35.81889299747227,33.15372056608939],[35.81871791771893,33.1537203155527]]] } },
  ],
};

export default function FluxMapView() {
  const [showUoH, setShowUoH] = useState(true);
  const [showTERRRA, setShowTERRRA] = useState(true);
  const [showTestArea, setShowTestArea] = useState(true);
  const [rasterOpacity, setRasterOpacity] = useState(0.75);
  const [inspectMode, setInspectMode] = useState(false);

  const [sidebarOpen, setSidebarOpen] = useState(() => {
    try {
      const saved = localStorage.getItem(SIDEBAR_OPEN_KEY);
      if (saved != null) return saved === '1';
    } catch (_) {}
    return typeof window !== 'undefined' ? window.innerWidth >= 768 : true;
  });
  const toggleSidebar = useCallback(() => {
    setSidebarOpen(v => {
      const next = !v;
      try { localStorage.setItem(SIDEBAR_OPEN_KEY, next ? '1' : '0'); } catch (_) {}
      return next;
    });
  }, []);

  const [uohUrl, setUohUrl] = useState('/v1/kkl/demo-raster');
  const [terrraPts, setTerrraPts] = useState(DUMMY_TERRRA_POINTS);
  const [testArea, setTestArea] = useState(DUMMY_TEST_AREA);
  const [rasterStats, setRasterStats] = useState(null);

  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const uohInputRef = useRef();
  const terrrraInputRef = useRef();
  const areaInputRef = useRef();

  const [exporting, setExporting] = useState(false);

  const handleUoHUpload = useCallback(async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true); setUploadError('');
    try {
      const fd = new FormData();
      fd.append('raster', file);
      const res = await fetch('/v1/kkl/upload-raster', { method: 'POST', body: fd });
      const data = await res.json();
      if (!data.ok) throw new Error(data.message || 'Upload failed');
      setUohUrl(data.url + '?t=' + Date.now());
    } catch (err) {
      setUploadError(err.message);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  }, []);

  const handleTERRRAUpload = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const gj = JSON.parse(ev.target.result);
        setTerrraPts(gj);
      } catch { setUploadError('Invalid GeoJSON for TERRRA points'); }
    };
    reader.readAsText(file);
    e.target.value = '';
  }, []);

  const handleAreaUpload = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const gj = JSON.parse(ev.target.result);
        setTestArea(gj);
      } catch { setUploadError('Invalid GeoJSON for test area'); }
    };
    reader.readAsText(file);
    e.target.value = '';
  }, []);

  const handleExportKML = useCallback(async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams({ rasterUrl: uohUrl });
      const res = await fetch(`/v1/kkl/export-kml?${params}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = 'kkl-carbon-model.kml';
      document.body.appendChild(a); a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      setUploadError('KML export failed: ' + err.message);
    } finally {
      setExporting(false);
    }
  }, [uohUrl]);

  const totalTons = rasterStats?.totalTons ?? null;
  const totalUSD = totalTons != null ? totalTons * CARBON_PRICE_USD : null;
  const totalILS = totalUSD != null ? totalUSD * ILS_PER_USD : null;

  return (
    <Box sx={{ display: 'flex', height: '100%', overflow: 'hidden', position: 'relative' }}>
      <Box sx={{
        width: sidebarOpen ? SIDEBAR_WIDTH : 0,
        flexShrink: 0, display: 'flex', flexDirection: 'column',
        borderRight: sidebarOpen ? '1px solid' : 'none', borderColor: 'divider',
        overflow: 'hidden', bgcolor: 'background.paper',
        transition: 'width 220ms ease',
      }}>
        <Box sx={{ width: SIDEBAR_WIDTH, height: '100%', display: 'flex', flexDirection: 'column', visibility: sidebarOpen ? 'visible' : 'hidden' }}>
          <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>עמק הבכא + בוסטר</Typography>
            <Typography variant="caption" color="text.secondary">KKL pilot parcels — ILSAR / Ramat HaGolan</Typography>
          </Box>
          <Box sx={{ flex: 1, overflowY: 'auto', p: 1.5 }}>
            {totalTons != null && (
              <Box sx={{ mb: 2, p: 1.5, bgcolor: 'success.lighter', borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary">Annual sequestration (UoH model)</Typography>
                <Typography variant="h6" color="success.dark" sx={{ fontWeight: 700 }}>
                  {totalTons.toFixed(0)} tCO₂/yr
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  ${totalUSD?.toFixed(0).toLocaleString()} / ₪{totalILS?.toFixed(0).toLocaleString()} gross @ $25/t
                </Typography>
              </Box>
            )}
            {uploadError && (
              <Alert severity="error" sx={{ mb: 1 }} onClose={() => setUploadError('')}>{uploadError}</Alert>
            )}
            <Typography variant="overline" color="text.secondary">Layers</Typography>
            <Stack spacing={0.5} sx={{ mb: 2 }}>
              <LayerToggle label="UoH Carbon Model (raster)" sublabel="10×10 m tCO₂/ha/yr" color="#4caf50" active={showUoH} onToggle={() => setShowUoH(v => !v)} />
              {showUoH && (
                <Box sx={{ px: 1.5, pb: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">Opacity</Typography>
                  <Slider size="small" min={0.1} max={1} step={0.05} value={rasterOpacity} onChange={(_, v) => setRasterOpacity(v)} />
                </Box>
              )}
              <LayerToggle label="TERRRA measurement points" sublabel="16 SequestOmeter units" color="#ff6b35" active={showTERRRA} onToggle={() => setShowTERRRA(v => !v)} />
              <LayerToggle label="KKL parcels (עמק הבכא / בוסטר)" sublabel="7 named plots from KML" color="#1565c0" active={showTestArea} onToggle={() => setShowTestArea(v => !v)} />
            </Stack>
            <Divider sx={{ my: 1.5 }} />
            <Typography variant="overline" color="text.secondary">Inspect</Typography>
            <Button fullWidth size="small" variant={inspectMode ? 'contained' : 'outlined'} color={inspectMode ? 'primary' : 'inherit'} startIcon={<Iconify icon="eva:pin-fill" />} onClick={() => setInspectMode(v => !v)} sx={{ mb: 2 }}>
              {inspectMode ? 'Inspecting pixels…' : 'Click pixel to inspect'}
            </Button>
            <Divider sx={{ my: 1.5 }} />
            <Typography variant="overline" color="text.secondary">Upload</Typography>
            <Stack spacing={1} sx={{ mb: 2 }}>
              <UploadButton label="UoH Model (GeoTIFF)" sublabel=".tif / .tiff" accept=".tif,.tiff" loading={uploading} inputRef={uohInputRef} onChange={handleUoHUpload} />
              <UploadButton label="TERRRA Points (GeoJSON)" sublabel=".geojson / .json" accept=".geojson,.json" inputRef={terrrraInputRef} onChange={handleTERRRAUpload} />
              <UploadButton label="Test Area (GeoJSON)" sublabel="Polygon boundary" accept=".geojson,.json" inputRef={areaInputRef} onChange={handleAreaUpload} />
            </Stack>
            <Divider sx={{ my: 1.5 }} />
            <Typography variant="overline" color="text.secondary">Export</Typography>
            <Button fullWidth size="small" variant="outlined" startIcon={exporting ? <CircularProgress size={14} /> : <Iconify icon="eva:download-fill" />} onClick={handleExportKML} disabled={exporting} sx={{ mb: 1 }}>
              Download results as KML
            </Button>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              KML includes tCO₂ and $ per 100×100 m pixel — open in Google Earth
            </Typography>
          </Box>
          <Box sx={{ p: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
            <Typography variant="caption" color="text.disabled">
              Demo data — replace UoH raster and TERRRA points with real measurements.
            </Typography>
          </Box>
        </Box>
      </Box>

      <Box sx={{ position: 'absolute', top: 12, left: sidebarOpen ? SIDEBAR_WIDTH - 14 : -2, zIndex: 1000, transition: 'left 220ms ease' }}>
        <Tooltip title={sidebarOpen ? 'Hide panel' : 'Show panel'}>
          <IconButton size="small" onClick={toggleSidebar} sx={{ bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', width: 28, height: 28, '&:hover': { bgcolor: 'background.paper' } }}>
            <Iconify icon={sidebarOpen ? 'eva:chevron-left-fill' : 'eva:chevron-right-fill'} sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      </Box>

      <Box sx={{ flex: 1, position: 'relative', minWidth: 0 }}>
        <MapContainer center={[33.168, 35.812]} zoom={14} style={{ width: '100%', height: '100%' }} zoomControl>
          <TileLayer url={SATELLITE_URL} attribution={SATELLITE_ATTR} />
          {showUoH && <UoHRasterLayer url={uohUrl} opacity={rasterOpacity} onStats={setRasterStats} />}
          {showTERRRA && <TERRRAPointsLayer geojson={terrraPts} />}
          {showTestArea && <StrataLayer geojson={testArea} />}
          <PixelInspector rasterStats={rasterStats} active={inspectMode} />
        </MapContainer>
        {inspectMode && (
          <Box sx={{ position: 'absolute', top: 12, left: '50%', transform: 'translateX(-50%)', zIndex: 1000, pointerEvents: 'none' }}>
            <Chip label="Click any pixel to inspect carbon value" color="primary" size="small" />
          </Box>
        )}
      </Box>
    </Box>
  );
}
